import { useCallback, useEffect, useRef } from 'react';
import { ReportData } from '@/components/preview/types';
import { isSessionExpired } from '@/lib/session-expiry';

type SaveCallback = () => void | Promise<void>;

interface UseAutoSaveProps {
    data: ReportData;
    onSave: SaveCallback;
    getComparableData?: (data: ReportData) => unknown;
    interval?: number; // defaults to 60000ms (1 min)
    enabled?: boolean;
}

export function useAutoSave({
    data,
    onSave,
    getComparableData,
    interval = 60000,
    enabled = true,
}: UseAutoSaveProps) {
    const onSaveRef = useRef<SaveCallback>(onSave);
    const getComparableDataRef = useRef(getComparableData);
    const serializeData = useCallback(
        (value: ReportData) =>
            JSON.stringify(
                getComparableDataRef.current ? getComparableDataRef.current(value) : value,
            ),
        [],
    );
    const savedDataRef = useRef<string>(serializeData(data));

    useEffect(() => {
        onSaveRef.current = onSave;
    }, [onSave]);

    useEffect(() => {
        getComparableDataRef.current = getComparableData;
    }, [getComparableData]);

    const checkAndSave = useCallback(async () => {
        if (!enabled) return;
        // Every save would 401 while the session-expired dialog is up, and
        // the failure is swallowed below — no point in the round trip.
        if (isSessionExpired()) return;

        const currentDataString = serializeData(data);
        if (currentDataString !== savedDataRef.current) {
            try {
                await onSaveRef.current();
                savedDataRef.current = currentDataString;
                console.log('Auto-saved successfully');
            } catch (error) {
                console.error('Auto-save failed:', error);
            }
        }
    }, [data, enabled, serializeData]);

    useEffect(() => {
        if (!enabled) return;

        const timer = setInterval(checkAndSave, interval);
        return () => clearInterval(timer);
    }, [checkAndSave, interval, enabled]);

    // Expose a manual trigger if needed, though not strictly part of "auto" save
    return { triggerSave: checkAndSave };
}
